/**
 * Vertical event timeline used by the payment-intent detail page.
 *
 * Chronological top→bottom (oldest first), each event a row with
 * sequence # gutter, event-type label, timestamp, and an inline
 * expandable payload preview.
 */
import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { JsonBlock } from "./JsonBlock";
import type { PaymentIntentEvent } from "@/lib/api";
import { cn } from "@/lib/utils";

export function Timeline({
  events,
  truncated,
}: {
  events: ReadonlyArray<PaymentIntentEvent>;
  truncated: boolean;
}) {
  if (events.length === 0) {
    return (
      <p className="rounded-md border border-dashed bg-muted/20 p-4 text-center text-sm text-muted-foreground">
        No events recorded yet.
      </p>
    );
  }

  // Ensure chronological order regardless of server ordering.
  const sorted = [...events].sort((a, b) => a.sequence - b.sequence);

  return (
    <div className="relative">
      <ol
        data-testid="event-log"
        className="relative space-y-3 border-l border-border pl-6"
      >
        {sorted.map((e) => (
          <TimelineRow key={e.sequence} event={e} />
        ))}
      </ol>
      {truncated ? (
        <p className="mt-3 text-xs text-muted-foreground">
          Truncated — older events available via the audit-log endpoint (future).
        </p>
      ) : null}
    </div>
  );
}

function TimelineRow({ event }: { event: PaymentIntentEvent }) {
  const [open, setOpen] = useState(false);
  const hasPayload = Object.keys(event.payload).length > 0;

  return (
    <li className="relative">
      <span
        aria-hidden
        className="absolute -left-[27px] top-1.5 flex h-3 w-3 items-center justify-center rounded-full border-2 border-background bg-primary"
      />
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Badge variant="muted" className="font-mono">
          #{event.sequence}
        </Badge>
        <span className="font-medium text-foreground">{event.event_type}</span>
        <span className="text-xs text-muted-foreground" title={event.created_at}>
          {new Date(event.created_at).toLocaleString()}
        </span>
      </div>
      {hasPayload ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-1.5 inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronRight
            className={cn(
              "h-3 w-3 transition-transform",
              open && "rotate-90",
            )}
          />
          {open ? "Hide payload" : "Show payload"}
        </button>
      ) : null}
      {open && hasPayload ? (
        <div className="mt-2">
          <JsonBlock value={event.payload} />
        </div>
      ) : null}
    </li>
  );
}
